import { useState } from 'react';
import { useCustom } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatDateOrNever } from '@/lib/utils';
import { PromotionStatusBadge } from '@/components/coupons/PromotionStatusBadge';
import { SOURCE_LABELS, issuanceLabel } from '@/lib/promotion-labels';
import type { PromotionSource, IssuanceType, PromotionStatus } from '@/lib/promotion-labels';
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  Gift,
} from 'lucide-react';

interface Promotion {
  id: string;
  name: string;
  description: string | null;
  source: PromotionSource;
  paywall_type: string;
  coupons_per_claim: number;
  issuance_type: IssuanceType;
  shareable_code: string | null;
  max_total_claims: number | null;
  max_claims_per_user: number;
  starts_at: string;
  ends_at: string | null;
  status: PromotionStatus;
  environment: 'dev' | 'prod';
  created_at: string;
  updated_at: string;
}

interface ListResponse {
  rows: Promotion[];
  nextCursor: string | null;
}

export function PromotionsListPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const queryParams: Record<string, string> = { limit: '50' };
  if (statusFilter) queryParams.status = statusFilter;
  if (sourceFilter) queryParams.source = sourceFilter;
  if (search) queryParams.q = search;
  if (cursor) queryParams.cursor = cursor;

  const { data, isLoading, isError } = useCustom<ListResponse>({
    url: '/v1/admin/promotions',
    method: 'get',
    config: {
      query: queryParams,
    },
  });

  const rows = data?.data?.rows ?? [];
  const nextCursor = data?.data?.nextCursor ?? null;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Gift className="w-6 h-6" />
            Promotions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Coupon-system campaigns. New paywalls, marketing codes, referral payouts.
          </p>
        </div>
        <button
          onClick={() => navigate('/promotions/new')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition"
        >
          <Plus className="w-4 h-4" />
          New Promotion
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCursor(undefined);
            }}
            className="w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-background"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCursor(undefined);
          }}
          className="px-3 py-2 border rounded-md text-sm bg-background"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="ended">Ended</option>
          <option value="archived">Archived</option>
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value);
            setCursor(undefined);
          }}
          className="px-3 py-2 border rounded-md text-sm bg-background"
        >
          <option value="">All sources</option>
          <option value="tester">Tester</option>
          <option value="marketing">Marketing</option>
          <option value="referral">Referral</option>
          <option value="support">Support</option>
          <option value="vip">VIP</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Source</th>
              <th className="px-4 py-3 font-medium">Paywall</th>
              <th className="px-4 py-3 font-medium">Issuance</th>
              <th className="px-4 py-3 font-medium text-right">Per claim</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Validity</th>
              <th className="px-4 py-3 font-medium w-12"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-destructive">
                  Failed to load promotions.
                </td>
              </tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  No promotions yet. Create one to get started.
                </td>
              </tr>
            )}
            {rows.map((p) => (
              <tr
                key={p.id}
                className="border-b hover:bg-muted/30 cursor-pointer transition"
                onClick={() => navigate(`/promotions/${p.id}`)}
              >
                <td className="px-4 py-3">
                  <div className="font-medium">{p.name}</div>
                  {p.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                      {p.description}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{SOURCE_LABELS[p.source] ?? p.source}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.paywall_type}</td>
                <td className="px-4 py-3 text-muted-foreground">
                  {issuanceLabel(p.issuance_type)}
                  {p.shareable_code && (
                    <code className="ml-2 px-1.5 py-0.5 rounded bg-muted text-xs">
                      {p.shareable_code}
                    </code>
                  )}
                </td>
                <td className="px-4 py-3 text-right">{p.coupons_per_claim}</td>
                <td className="px-4 py-3">
                  <PromotionStatusBadge status={p.status} />
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                  {formatDate(p.starts_at)} → {formatDateOrNever(p.ends_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <Eye className="w-4 h-4 inline text-muted-foreground" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(cursor !== undefined || nextCursor) && (
        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            disabled={cursor === undefined}
            onClick={() => setCursor(undefined)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            First page
          </button>
          <button
            type="button"
            disabled={!nextCursor}
            onClick={() => {
              if (nextCursor) setCursor(nextCursor);
            }}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
